import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, LayoutDashboard, Plus, Trash2 } from '../../icons';
import { Badge, Button, EmptyState, Select, Switch } from '../../components/shared';
import {
  listAdminTemplates, saveTemplate,
  type DashboardTemplateDto, type LoginLanding, type TabPosition,
  type TemplateKind, type TemplateSpan, type TemplateWidget,
} from '../../api/dashboard-templates';
import { getWidgetDef } from '../DashboardPage/board/widget-registry';
import { WidgetChromeContext, DEFAULT_CHROME } from '../DashboardPage/board/widget-chrome';
import { WidgetActionsContext, type WidgetActions } from '../DashboardPage/board/widget-context';
import { WidgetPickerModal } from '../DashboardPage/modals/WidgetPickerModal';
import '../DashboardPage/board/board.css';
import './NoticeSlide.css';
import './WidgetLayout.css';

/**
 * 통합설정 › 대시보드 › **위젯 관리** (2026-09-08 · migration 173 · 시안 화면 I).
 *
 * ★★★ **여기서 정하는 것은 「전사 대시보드」 하나다** (2026-09-09 사용자 지시).
 *     종전에는 관리 대상이 셋이었다 — 전사 · 신규 사용자 기본 · 부서별 기본. 뒤의 둘은
 *     **«사용자의» 위젯**을 회사가 미리 채워 두는 것이었는데, 그 둘을 걷어냈다.
 *     · **개인 대시보드는 사용자의 것**이다 — 회사가 남의 배치를 미리 정해 두고 덮어쓰는
 *       길이 있으면 언젠가 남의 화면이 지워진다.
 *     · 개인 배치는 서버가 아니라 **각자의 브라우저**에 있어서, 회사가 「덮어쓰기」를 걸어도
 *       *지금 당장*이 아니라 각자 다음에 열 때였다 — 설명하기 어렵고 되돌릴 수 없었다.
 *     · 실측(2026-09-09 운영): `dashboard_templates` 에 **행이 하나도 없었다** — 신규/부서
 *       기본판을 쓰는 회사가 0 이다. 아무도 안 쓰는 길을 두느니 없앤다.
 *
 * ★★★ **그래서 「덮어쓰기」도 없앴다.** 전사 대시보드는 **잠긴 탭**이라 열 때마다 회사가 정한
 *     배치로 다시 그려진다 — 덮어쓸 것이 애초에 없다. 눌러도 아무 일이 없는 단추였다.
 *
 * ★★ 정책 넷은 그대로다(사용 · 로그인 첫 화면 · 사용자가 숨기기 · 탭 위치).
 *    「사용자가 숨기기」·「탭 위치」는 판 2 에서 일부러 뺐다가 입구(탭 ⋯ 메뉴)가 생긴 뒤
 *    판 4b 에서 열었다 — 켜면 실제로 달라진다(migration 175).
 */

/** 여기서 다루는 유일한 대상. 서버 타입은 셋을 알지만 **이 화면은 전사만 쓴다.** */
const KIND: TemplateKind = 'CORP';

const LANDING_OPTIONS: Array<{ value: LoginLanding; label: string }> = [
  { value: 'CORP', label: '전사' },
  { value: 'PERSONAL', label: '나의 대시보드' },
  { value: 'LAST', label: '마지막에 본 것' },
];

/** 배치 미리보기 안의 행동 — 전부 아무 일도 하지 않는다(여기는 정하는 자리다). */
const INERT_ACTIONS: WidgetActions = { go: () => {}, openAttendanceRequest: () => {}, openSettings: () => {} };

const SPAN_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '4', label: '1/3' }, { value: '6', label: '1/2' },
  { value: '8', label: '2/3' }, { value: '12', label: '전체' },
];

export function WidgetLayoutSection() {
  const [rows, setRows] = useState<DashboardTemplateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  /** 저장 전 배치 — 서버 값과 따로 들고 있다가 「저장」에서 한 번에 보낸다. */
  const [draft, setDraft] = useState<TemplateWidget[]>([]);
  const [dirty, setDirty] = useState(false);

  const cur = useMemo(() => rows.find((r) => r.kind === KIND) ?? null, [rows]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listAdminTemplates();
      setRows(list);
      setError(null);
    } catch {
      setError('기본판을 불러오지 못했습니다');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    setDraft(cur?.widgets ?? []);
    setDirty(false);
    setNotice(null);
  }, [cur]);

  const patch = async (input: Parameters<typeof saveTemplate>[1], msg?: string) => {
    setBusy(true);
    try {
      const saved = await saveTemplate(KIND, input);
      setRows((list) => {
        const i = list.findIndex((r) => r.kind === KIND);
        if (i < 0) return [...list, saved];
        const next = [...list];
        next[i] = saved;
        return next;
      });
      if (msg) setNotice(msg);
      setError(null);
      return true;
    } catch {
      setError('저장하지 못했습니다');
      return false;
    } finally { setBusy(false); }
  };

  const saveLayout = async () => {
    /* ★ 전사 탭은 «잠긴 탭»이라 열 때마다 이 배치로 다시 그려진다 — 「새로 들어오는 사람만」이
       아니라 **모두가 다음에 열 때** 받는다. 종전 문구는 신규 기본판 시절의 말이었다. */
    const ok = await patch({ widgets: draft }, '배치를 저장했습니다 — 모두가 다음에 열 때 이 배치를 받습니다');
    if (ok) setDirty(false);
  };

  const setSpan = (index: number, span: TemplateSpan) => {
    setDraft((d) => d.map((x, i) => (i === index ? { ...x, span } : x)));
    setDirty(true);
  };
  const removeAt = (index: number) => {
    setDraft((d) => d.filter((_, i) => i !== index));
    setDirty(true);
  };
  const move = (index: number, dir: -1 | 1) => {
    setDraft((d) => {
      const to = index + dir;
      if (to < 0 || to >= d.length) return d;
      const next = [...d];
      [next[index]!, next[to]!] = [next[to]!, next[index]!];
      return next;
    });
    setDirty(true);
  };
  /**
   * ★★★ **대시보드와 «같은 창»을 쓴다** (2026-09-09 사용자 지시).
   *   종전에는 이 화면만 «펼쳐지는 단추 목록» 이라, 이름과 한 줄 곁말만 보고 골라야 했다.
   *   이제 둘 다 정본 F1 `picker` 창을 열고 **오른쪽에 진짜 위젯**이 선다.
   * ★ 한 번에 여럿을 담을 수 있다 — 종전에는 하나 누를 때마다 목록이 닫혔다.
   */
  const addWidgets = (keys: string[]) => {
    setDraft((d) => [
      ...d,
      ...keys.map((key) => ({ key, span: (getWidgetDef(key)?.defaultSpan ?? 4) as TemplateSpan })),
    ]);
    setDirty(true);
    setAdding(false);
  };

  return (
    <section className="svc-cat svc-cat--menu">
      <header className="svc-cat__head">
        <span className="svc-cat__icon"><LayoutDashboard size={17} /></span>
        <span className="svc-cat__title">위젯 관리</span>
        <span className="svc-cat__desc">
          회사가 정하는 <b>전사 대시보드</b> — 모두의 탭에 잠긴 채로 나타납니다
        </span>
      </header>

      {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}
      {notice && <div className="admin-console__alert" role="status">{notice}</div>}

      <div className="wl-one">
        <div className="nsl-work">
          <div className="nsl-work__head">
            <span className="nsl-work__name">전사 대시보드</span>
            {cur && (cur.enabled
              ? <Badge variant="success" size="sm">켜짐</Badge>
              : <Badge size="sm">꺼짐</Badge>)}
            {!cur && <Badge variant="warning" size="sm">아직 없음</Badge>}
            <span className="nsl-work__sp" />
            <span className="wl-count">위젯 {draft.length}개</span>
            <Button variant="primary" size="sm" disabled={busy || !dirty} onClick={() => { void saveLayout(); }}>
              저장
            </Button>
          </div>
          <p className="wl-sub">
            모든 사용자의 탭에 나타납니다 · 사용자는 편집할 수 없습니다 —
            <b> 열 때마다 여기 배치로 다시 그려집니다.</b>
          </p>

          {/* ── 위젯 배치 ─────────────────────────────────────────── */}
          <div className="wl-block">
            <div className="wl-block__head">
              <h4 className="wl-block__h">위젯 배치</h4>
              <span className="wl-block__hint">보이는 그대로 나갑니다 · 12칸 격자</span>
              <span className="wl-block__n">{draft.length}개</span>
            </div>

            {loading
              ? <p className="wl-empty">불러오는 중…</p>
              : draft.length === 0
                ? (
                  <EmptyState
                    icon={LayoutDashboard}
                    title={cur ? '위젯이 없습니다' : '아직 정하지 않았습니다'}
                    description={cur
                      ? '위젯을 놓으면 이 대상이 그 배치를 받습니다.'
                      : '위젯을 놓고 저장하면 그때부터 이 대상에 적용됩니다. 정하기 전까지는 앱에 박힌 기본 배치가 쓰입니다.'}
                  />
                )
                : (
                  /*
                   * ★★★ **대시보드와 «같은 격자»로 그린다** (2026-09-09 사용자 지시).
                   *   종전에는 번호가 붙은 «목록»이라, 1/3 을 셋 놓았을 때 그것이 한 줄이 되는지
                   *   세 줄이 되는지 **저장해서 대시보드를 열어 봐야** 알 수 있었다.
                   *   이제 12칸 격자에 **진짜 위젯**이 제 폭으로 선다 — 보이는 그대로 나간다.
                   * ★ 위젯 안에서는 아무 데도 못 간다(행동을 빈 함수로 갈아 끼운다) — 여기는
                   *   «배치를 정하는 자리»이지 쓰는 자리가 아니다.
                   */
                  <div className="dw-grid wl-grid">
                    {draft.map((x, i) => {
                      const def = getWidgetDef(x.key);
                      return (
                        <div className={`dw-slot dw-slot--${x.span} wl-cell`} key={`${x.key}-${i}`}>
                          <div className="wl-cell__stage">
                            {def
                              ? (
                                <WidgetActionsContext.Provider value={INERT_ACTIONS}>
                                  <WidgetChromeContext.Provider value={DEFAULT_CHROME}>
                                    <def.Component />
                                  </WidgetChromeContext.Provider>
                                </WidgetActionsContext.Provider>
                              )
                              : (
                                /* ★ 레지스트리에 없는 key 도 지우지 않고 그대로 보여 준다 —
                                     서버·프론트 배포 순서가 어긋난 동안 배치를 갉아먹지 않게. */
                                <div className="wl-cell__unknown">
                                  <b>{x.key}</b>
                                  <span className="wl-item__unknown">이 판에 없는 위젯</span>
                                </div>
                              )}
                          </div>

                          <div className="wl-cell__bar">
                            <span className="wl-cell__n">{i + 1}</span>
                            <span className="wl-cell__name">{def?.label ?? x.key}</span>
                            <Select
                              ariaLabel={`${def?.label ?? x.key} 폭`}
                              value={String(x.span)}
                              onChange={(v) => setSpan(i, Number(v) as TemplateSpan)}
                              options={SPAN_OPTIONS}
                              size="sm"
                              disabled={busy}
                              portal
                            />
                            <Button variant="secondary" size="sm" disabled={i === 0 || busy}
                              aria-label={`${def?.label ?? x.key} 위로`} onClick={() => move(i, -1)}>↑</Button>
                            <Button variant="secondary" size="sm" disabled={i === draft.length - 1 || busy}
                              aria-label={`${def?.label ?? x.key} 아래로`} onClick={() => move(i, 1)}>↓</Button>
                            <Button variant="secondary" size="sm" iconOnly disabled={busy}
                              aria-label={`${def?.label ?? x.key} 빼기`} onClick={() => removeAt(i)}>
                              <Trash2 size={14} aria-hidden />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

            <Button variant="secondary" size="sm" iconLeft={<Plus size={14} />} disabled={busy} onClick={() => setAdding(true)}>
              위젯 추가
            </Button>

            {adding && (
              <WidgetPickerModal
                alreadyOn={draft.map((x) => x.key)}
                confirmLabel="기본판에 넣기"
                onConfirm={addWidgets}
                onClose={() => setAdding(false)}
              />
            )}
          </div>

          {/* ── 정책 ─────────────────────────────────────────────── */}
          <div className="wl-block">
            <div className="wl-block__head">
              <h4 className="wl-block__h">정책</h4>
              <span className="wl-block__hint">사용자 쪽에서 이 기본판이 어떻게 보일지</span>
            </div>

            <div className="nsl-rules__row">
              <span className="nsl-rules__k">사용</span>
              <Switch
                checked={cur?.enabled ?? true}
                onCheckedChange={(v) => { void patch({ enabled: v }); }}
                label={(cur?.enabled ?? true) ? '켬' : '끔'}
                disabled={busy || !cur}
              />
              <span className="nsl-rules__note">끄면 전사 탭이 모두의 화면에서 사라집니다.</span>
            </div>

            <div className="nsl-rules__row">
              <span className="nsl-rules__k">로그인 첫 화면</span>
              <Select
                ariaLabel="로그인 첫 화면"
                value={cur?.loginLanding ?? 'LAST'}
                onChange={(v) => { void patch({ loginLanding: v as LoginLanding }); }}
                options={LANDING_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                size="sm"
                disabled={busy || !cur}
              />
              <span className="nsl-rules__note">
                사용자가 개인 설정에서 다시 고를 수 있습니다 — 여기 값은 <b>기본값</b>입니다.
              </span>
            </div>

            <div className="nsl-rules__row">
              <span className="nsl-rules__k">사용자가 숨기기</span>
              <Switch
                checked={cur?.userCanHide ?? true}
                onCheckedChange={(v) => { void patch({ userCanHide: v }); }}
                label={(cur?.userCanHide ?? true) ? '탭 ⋯ 메뉴에서 숨길 수 있습니다' : '항상 보입니다'}
                disabled={busy || !cur}
              />
              <span className="nsl-rules__note">
                끄면 항상 보입니다. <b>필독 공지를 띄우는 기간에만 잠그는 식</b>으로 씁니다 —
                끄면 이미 숨겨 둔 사람의 탭도 다시 나타납니다.
              </span>
            </div>

            <div className="nsl-rules__row">
              <span className="nsl-rules__k">탭 위치</span>
              <Select
                ariaLabel="탭 위치"
                value={cur?.tabPosition ?? 'FREE'}
                onChange={(v) => { void patch({ tabPosition: v as TabPosition }); }}
                options={[{ value: 'FIRST', label: '맨 앞 고정' }, { value: 'FREE', label: '사용자가 옮길 수 있음' }]}
                size="sm"
                disabled={busy || !cur}
              />
              <span className="nsl-rules__note">
                「맨 앞 고정」이면 사용자의 <b>탭 순서 바꾸기가 통째로 잠깁니다</b>.
              </span>
            </div>
          </div>

          {/* ★★★ 종전에 있던 「이미 쓰는 사람에게 덮어쓰기」는 **없앴다** (2026-09-09).
                 전사 대시보드는 잠긴 탭이라 열 때마다 이 배치로 다시 그려진다 — 덮어쓸 것이
                 애초에 없었고, 눌러도 아무 일이 일어나지 않는 단추였다. */}
          <div className="wl-danger wl-danger--calm">
            <AlertTriangle size={18} aria-hidden />
            <div>
              <b>개인 대시보드는 건드리지 않습니다.</b> 여기서 정하는 것은 <b>전사 탭 하나</b>이고,
              사람들이 각자 꾸민 「나의 대시보드」는 그대로 남습니다.
              <p className="wl-danger__how">
                전사 탭은 <b>잠겨 있어</b> 사용자가 고칠 수 없고, 열 때마다 여기 배치로 다시
                그려집니다 — 따로 「덮어쓰기」를 누를 일이 없습니다.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
